/**
 * Contact display helpers — initials, deterministic colors, status pill.
 *
 * Used across Contacts list and ContactDetail to keep visual treatment
 * consistent. Pure-render utilities — no state, no side effects.
 */
import { Briefcase, Snowflake, Flame, ThumbsUp, X } from 'lucide-react';


// ─────────── initials ───────────

export function getInitials(firstName, lastName, fallbackEmail) {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f || l) {
    return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?';
  }
  if (fallbackEmail) return fallbackEmail.slice(0, 2).toUpperCase();
  return '?';
}


// ─────────── deterministic color per name ───────────
//
// Hash the name into one of the brand-palette tones (gold + charcoals).
// Same name → same color across sessions. Fully cosmetic.
const AVATAR_PALETTE = [
  { bg: '#B89B5E', fg: '#1A1A1A' }, // gold
  { bg: '#1F2937', fg: '#F5F2EB' }, // charcoal
  { bg: '#374151', fg: '#F5F2EB' }, // dark slate
  { bg: '#92652C', fg: '#F5F2EB' }, // antique gold
  { bg: '#0F172A', fg: '#F5F2EB' }, // deep navy
  { bg: '#52525B', fg: '#F5F2EB' }, // zinc
  { bg: '#86742F', fg: '#F5F2EB' }, // olive gold
];


function _hash(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}


export function getAvatarColor(name) {
  const idx = _hash((name || '').toLowerCase()) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}


// ─────────── relationship status ───────────

export const RELATIONSHIP_STATUSES = [
  { id: 'unknown', label: 'Unknown', dot: '#71717A', icon: null },
  { id: 'cold',    label: 'Cold',    dot: '#64748B', icon: Snowflake },
  { id: 'warm',    label: 'Warm',    dot: '#F59E0B', icon: ThumbsUp },
  { id: 'hot',     label: 'Hot',     dot: '#B89B5E', icon: Flame },
  { id: 'client',  label: 'Client',  dot: '#10B981', icon: Briefcase },
  { id: 'lost',    label: 'Lost',    dot: '#EF4444', icon: X },
];


export function getRelationshipStatus(id) {
  return RELATIONSHIP_STATUSES.find((s) => s.id === id) || RELATIONSHIP_STATUSES[0];
}


// ─────────── last activity formatter ───────────

export function formatLastActive(iso) {
  if (!iso) return 'Never contacted';
  try {
    const then = new Date(iso);
    const now = new Date();
    const diffMs = now - then;
    if (Number.isNaN(diffMs) || diffMs < 0) return 'Never contacted';
    const sec = Math.floor(diffMs / 1000);
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);
    const day = Math.floor(hr / 24);
    if (sec < 60) return 'Just now';
    if (min < 60) return `${min}m ago`;
    if (hr < 24) return `${hr}h ago`;
    if (day === 1) return 'Yesterday';
    if (day < 30) return `${day}d ago`;
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo}mo ago`;
    const yr = Math.floor(day / 365);
    return `${yr}y ago`;
  } catch {
    return 'Never contacted';
  }
}
